import { ContactUsFormSchema, ContactUsSchema } from './contact-us-schema';
import { ReCaptcha } from './recaptcha';
import { headlessPageDataAtom, parseSanitizeItem } from '@exploration/ui-utils';
import { MDSButton } from '@midwest/web/button';
import {
  MDSPhoneInputForm,
  MDSTextAreaForm,
  MDSTextInputForm,
} from '@midwest/web/forms';
import { Tracking, useAnalyticsTracking } from '@thrivent-web/analytics';
import { logger } from '@thrivent-web/logging-utils';
import { desktop } from '@thrivent/midwest-shared';
import { MDSText } from '@thrivent/midwest-web';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useLeadFormContextData } from 'visitor-attributes-react';

// --------------------------------------------------------------------------

const FORM_NAME = 'Contact Us'; // v26
const FORM_GOAL = 'Contact'; // v53
// This ID was stolen from the BS form
const FORM_ID = '00000186-a8eb-d760-a7cf-a8efd4930000'; //v107

// --------------------------------------------------------------------------

const ContactUsForm = styled.form`
  display: grid;
  width: 100%;
  grid-gap: 1.25rem;

  ${desktop} {
    grid-template-columns: repeat(6, 1fr);
    max-width: 35rem;
  }
`;

const ContactUsFieldSpacer = styled.div`
  display: none;
  ${desktop} {
    display: block;
    grid-column: span 2;
  }
`;

const ContactUsFieldFull = styled(MDSTextInputForm)`
  ${desktop} {
    grid-column: span 6;
  }
`;

const ContactUsFieldPhone = styled(MDSPhoneInputForm)`
  ${desktop} {
    grid-column: span 2;
  }
`;

const ContactUsFieldZip = styled(MDSTextInputForm)`
  ${desktop} {
    grid-column: span 3;
  }
`;

const ContactUsMessage = styled(MDSTextAreaForm)`
  ${desktop} {
    grid-column: span 6;
  }
`;

const ContactUsSubmit = styled(MDSButton)`
  margin-top: 2rem;
  padding-inline: 3rem;
`;

const ContactUsSubmitWrapper = styled.div`
  ${desktop} {
    grid-column: span 8;
  }
`;

// Note: If TextInterfaceRegularXs ever appears in MDS, this should be switched to that
const DisclaimerText = styled(MDSText.BodyRegXs)`
  color: ${(p) => p.theme.midwestColors.neutralDarkMedium};
  display: block;
  max-width: 29rem;
  line-height: 1rem;
  margin-top: 1rem;
`;

const PrivacyPolicyText = styled(MDSText.BodyRegXs)`
  margin-top: 2rem;
  display: block;
  max-width: 29rem;

  > a {
    font-size: 1em;
    text-decoration: underline;
  }
`;

const RootError = styled(MDSText.BodyMediumSm)`
  color: ${(p) => p.theme.midwestColors.formColorInvalid};
  margin-top: 1rem;
  display: block;
`;

// --------------------------------------------------------------------------

export const Form = () => {
  const SUBMIT_URL =
    process.env['NEXT_PUBLIC_CONTACT_US_SUBMIT_URL'] ??
    'NEXT_PUBLIC_CONTACT_US_SUBMIT_URL_NOT_SET';
  const pageData = useAtomValue(headlessPageDataAtom);
  const {
    utmSourceDTM,
    utmSourceURLDTM,
    referenceCodeURL,
    referenceCodeReferrer,
    landingPageQueryParams,
    landingPageBaseURL,
  } = useLeadFormContextData();
  const { text, callToAction, image } = pageData ?? {};
  const form = useFormContext<ContactUsFormSchema>();
  const errorRef = useRef<HTMLSpanElement | null>(null);
  const { trackEvent } = useAnalyticsTracking();

  useEffect(() => {
    // Preload the confirmation image, so it doesn't "pop" in on first load
    const img = new Image();
    img.src = image?.['confirmation-success']?.src;
  }, [image]);

  const handleSubmit = async (data: ContactUsFormSchema) => {
    try {
      // Bail if we somehow got here mid submit
      if (!!form?.formState?.isSubmitting) {
        return;
      }
      // Bail if we somehow got here with bad data
      const result = ContactUsSchema.safeParse(data);
      if (!result.success) {
        form.setError('root', result.error);
        return;
      }
      const bodyFormData = new FormData();
      for (const key in data) {
        bodyFormData.append(key, `${data[key as keyof ContactUsFormSchema]}`);
      }

      // Add in WIS data
      bodyFormData.append('utmSourceDTM', utmSourceDTM);
      bodyFormData.append('utmSourceURLDTM', utmSourceURLDTM);
      bodyFormData.append('referenceCodeURL', referenceCodeURL);
      bodyFormData.append('referenceCodeReferrer', referenceCodeReferrer);
      bodyFormData.append('landingPageQueryParams', landingPageQueryParams);
      bodyFormData.append('landingPageBaseURL', landingPageBaseURL);

      await axios
        .post(SUBMIT_URL, bodyFormData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })
        .then((e) => {
          if (e.status !== 200) {
            logger.error('Contact Us Submit: non 200', e);
            form.setError('root', {
              message: 'Please call the Client Care Center at 800-847-4836',
            });
            return;
          }
          // If we got here, submit a success to analytics
          trackEvent({
            eventType: Tracking.formSubmitSuccess,
            eventInfo: {
              formName: FORM_NAME,
              formGoal: FORM_GOAL,
              formId: FORM_ID,
            },
          });
        })
        .catch((e) => {
          if (
            e?.response?.data?.errors?.length &&
            Array.isArray(e?.response?.data?.errors)
          ) {
            form.setError('root', { message: e.response.data.errors[0] });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            form.setError('root', e);
          }

          if (errorRef?.current) {
            errorRef.current.focus();
          }
        });
    } catch (e) {
      logger.error('Contact Us Submit: Caught Error', e);
      form.setError('root', {
        message: 'Please call the Client Care Center at 800-847-4836',
      });
      if (errorRef?.current) {
        errorRef.current.focus();
      }
    }
  };

  return (
    <>
      <MDSText.TitleSm>
        {parseSanitizeItem(text?.['form-title'])}
      </MDSText.TitleSm>
      <ContactUsForm
        action={SUBMIT_URL}
        method="POST"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-analytics-name={FORM_NAME}
        data-analytics-form-goal={FORM_GOAL}
        data-analytics-id={FORM_ID}
      >
        <ContactUsFieldFull name="r_First_Name" label="First name" required />
        <ContactUsFieldFull name="r_Last_Name" label="Last name" required />
        <ContactUsFieldFull
          name="r_Email"
          label="Email"
          inputType="email"
          required
        />
        <ContactUsFieldSpacer />
        <ContactUsFieldPhone
          onChange={() => null}
          name="r_Phone"
          label="Mobile Number"
          placeholder="(xxx) xxx-xxxx"
          format="(###) ###-####"
          required
        />
        <ContactUsFieldZip
          name="r_ZIP"
          label="ZIP Code"
          inputType="number"
          required
        />
        <input type="hidden" name="r_topic" value="another-topic" />
        <ContactUsMessage name="another-topic" label="Message" />
        <ContactUsSubmitWrapper>
          <ReCaptcha name="g-recaptcha-response" setValue={form.setValue} />
          <DisclaimerText as="small">
            {parseSanitizeItem(text?.['form-disclaimer-text'])}
          </DisclaimerText>

          {!!form?.formState?.errors?.root?.message && (
            <RootError role="alert" tabIndex="0" ref={errorRef}>
              There was an error with your submission:{' '}
              {form.formState.errors.root?.message}
            </RootError>
          )}

          <ContactUsSubmit
            type="submit"
            loading={!!form?.formState?.isSubmitting}
            disabled={!!form?.formState?.isSubmitting}
          >
            {callToAction?.['form-submit-button']?.text}
          </ContactUsSubmit>
          <PrivacyPolicyText>
            {parseSanitizeItem(text?.['privacy-policy-text'])}
          </PrivacyPolicyText>
        </ContactUsSubmitWrapper>
      </ContactUsForm>
    </>
  );
};
